import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { createSafeHTML } from '@/utils/sanitizeHtml';

// Mock blog post data - in a real app, this would come from an API
const blogPosts = [
  {
    id: '1',
    title: 'Starting Your SaaS Reseller Business: A Complete Guide',
    content: `
      <p class="lead">The SaaS reseller market is experiencing unprecedented growth, with more entrepreneurs recognizing the potential of selling white-labeled software solutions.</p>
      
      <h2>Why Become a SaaS Reseller?</h2>
      <p>Starting a SaaS reseller business offers several advantages compared to developing your own software from scratch:</p>
      <ul>
        <li>Lower barrier to entry with minimal upfront investment</li>
        <li>Quick time-to-market with ready-made solutions</li>
        <li>Established product reliability and support infrastructure</li>
        <li>Ability to focus on your core competency: sales and customer relationships</li>
      </ul>
      
      <h2>Finding the Right Niche</h2>
      <p>The most successful resellers target specific industries or business needs. Consider these factors when selecting your niche:</p>
      <ol>
        <li>Industry expertise or connections you already possess</li>
        <li>Market gaps where businesses are underserved</li>
        <li>Growing sectors with increasing software adoption</li>
        <li>Complementary services you can offer alongside the software</li>
      </ol>
      
      <h2>Selecting a Reseller Program</h2>
      <p>Not all reseller programs are created equal. Look for these qualities in a potential partner:</p>
      <ul>
        <li>Comprehensive white-labeling capabilities</li>
        <li>Strong revenue-sharing models</li>
        <li>Reliable product with positive customer reviews</li>
        <li>Robust customer support and training resources</li>
        <li>Marketing materials and sales enablement tools</li>
      </ul>
      
      <h2>Building Your Brand</h2>
      <p>Even though you're reselling someone else's technology, your brand represents your unique value proposition:</p>
      <ul>
        <li>Develop a compelling brand identity that resonates with your target market</li>
        <li>Customize the software interface with your branding elements</li>
        <li>Create marketing materials that emphasize your unique selling points</li>
        <li>Focus on the outcomes and benefits rather than just features</li>
      </ul>
      
      <h2>Pricing Strategies</h2>
      <p>Your pricing structure will significantly impact your profitability and market position:</p>
      <ul>
        <li>Research competitor pricing in your niche</li>
        <li>Consider tiered pricing based on features or usage</li>
        <li>Evaluate subscription models vs. one-time purchases</li>
        <li>Factor in your value-added services when setting prices</li>
        <li>Build in room for promotional offers without hurting margins</li>
      </ul>
      
      <h2>Marketing Your Reseller Business</h2>
      <p>With your solution ready to go, focus on these proven marketing strategies:</p>
      <ul>
        <li>Content marketing: blogs, case studies, and whitepapers</li>
        <li>Search engine optimization for industry-specific keywords</li>
        <li>Targeted digital advertising campaigns</li>
        <li>Industry partnerships and referral networks</li>
        <li>Free trials or freemium models to reduce adoption barriers</li>
      </ul>
      
      <h2>Scaling Your Business</h2>
      <p>As you gain traction, consider these avenues for growth:</p>
      <ul>
        <li>Expand your service offerings with complementary software</li>
        <li>Develop expertise in adjacent industries</li>
        <li>Build a team of specialized sales representatives</li>
        <li>Create implementation and training services</li>
        <li>Establish customer success programs to drive retention</li>
      </ul>
    `,
    category: 'business',
    date: '2025-04-20',
    author: 'Sarah Johnson',
    authorTitle: 'Business Development Director',
    authorImage: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png',
    image: '/lovable-uploads/5df22fbf-46c0-422b-a964-30ad729c926d.png',
    readTime: '8 min'
  },
  {
    id: '2',
    title: 'How to Scale Your Referral Program as a Reseller',
    content: `
      <p class="lead">A well-designed referral program can be the growth engine for your SaaS reseller business, creating a sustainable pipeline of qualified leads.</p>
      
      <h2>Why Referral Programs Work for Resellers</h2>
      <p>Referral marketing is particularly effective in the SaaS reseller space because:</p>
      <ul>
        <li>Software purchases are often high-consideration decisions influenced by trusted recommendations</li>
        <li>Satisfied customers can easily articulate the benefits they've experienced</li>
        <li>The recurring revenue model creates long-term value from each referral</li>
        <li>Referred customers typically have higher retention rates and lifetime values</li>
      </ul>
      
      <h2>Setting Up Your Program Foundation</h2>
      <p>Before scaling your referral efforts, ensure you have these fundamentals in place:</p>
      <ol>
        <li>Clear, compelling incentives for both referrers and new customers</li>
        <li>Simple process for making and tracking referrals</li>
        <li>Automated rewards fulfillment system</li>
        <li>Legal framework and terms for the program</li>
      </ol>
      
      <h2>Optimizing Incentive Structures</h2>
      <p>The right incentives can dramatically increase participation:</p>
      <ul>
        <li>Consider two-sided rewards that benefit both parties</li>
        <li>Align incentives with your product's value (percentage of subscription, service credits, etc.)</li>
        <li>Test different reward levels to find the sweet spot</li>
        <li>Create tiered rewards for multiple or high-value referrals</li>
        <li>Implement time-limited promotions for referral campaigns</li>
      </ul>
      
      <h2>Promoting Your Referral Program</h2>
      <p>Even the best program needs visibility to gain traction:</p>
      <ul>
        <li>Integrate referral call-to-actions throughout the customer journey</li>
        <li>Highlight success stories from customers who've earned rewards</li>
        <li>Train your customer success team to mention the program at key touchpoints</li>
        <li>Include referral information in regular communications</li>
        <li>Create dedicated email campaigns to boost participation</li>
      </ul>
      
      <h2>Leveraging Technology</h2>
      <p>The right tools can help you scale efficiently:</p>
      <ul>
        <li>Implement referral tracking software that integrates with your CRM</li>
        <li>Use automated emails to nurture referrals through the pipeline</li>
        <li>Deploy analytics to identify your most valuable referrers</li>
        <li>Test A/B variations of referral page designs and messaging</li>
        <li>Create personalized referral links for easy sharing</li>
      </ul>
      
      <h2>Activating Partner Networks</h2>
      <p>Beyond individual customers, consider these partnership approaches:</p>
      <ul>
        <li>Develop formal affiliate programs for industry influencers</li>
        <li>Create co-marketing opportunities with complementary service providers</li>
        <li>Establish referral relationships with consultants in your target industries</li>
        <li>Offer enhanced commissions for high-volume referral partners</li>
      </ul>
      
      <h2>Measuring and Optimizing</h2>
      <p>Track these key metrics to continually improve your program:</p>
      <ul>
        <li>Participation rate among eligible customers</li>
        <li>Referral conversion rate</li>
        <li>Customer acquisition cost via referrals vs. other channels</li>
        <li>Lifetime value of referred customers</li>
        <li>ROI of referral program incentives</li>
        <li>Time from referral to conversion</li>
      </ul>
    `,
    category: 'marketing',
    date: '2025-04-15',
    author: 'David Chen',
    authorTitle: 'Marketing Strategy Consultant',
    authorImage: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png',
    image: '/lovable-uploads/74192c5a-ba76-4e08-9c4a-f98e75942d6c.png',
    readTime: '6 min'
  },
  // Other blog posts would be included here
];

const BlogPostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const post = blogPosts.find(post => post.id === id);
  
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header isLoggedIn={false} />
        <main className="flex-grow py-8 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
            <p className="mb-6">The blog post you're looking for doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/blog')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Return to Blog
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`${post.title} | SaaS Reseller Blog`}
        description={post.content.substring(0, 160).replace(/<[^>]*>/g, '')}
        keywords={`${post.category}, saas reseller, white label, referral program, ${post.title.toLowerCase()}`}
        ogImage={post.image}
      />
      <div className="min-h-screen flex flex-col">
        <Header isLoggedIn={false} />
        <main className="flex-grow py-8 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/blog')}
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
            </Button>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
              <img 
                src={post.image} 
                alt={post.title} 
                className="w-full h-64 object-cover object-center"
              />
              
              <div className="p-8">
                <div className="flex justify-between items-center mb-4">
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-medium">
                    {post.category}
                  </span>
                  <span className="text-gray-500 text-sm">{post.date} • {post.readTime} read</span>
                </div>
                
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{post.title}</h1>
                
                <div 
                  className="prose prose-lg max-w-none" 
                  dangerouslySetInnerHTML={createSafeHTML(post.content)}
                />
              </div>
            </div>
            
            <Card className="mb-8">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <img 
                    src={post.authorImage} 
                    alt={post.author} 
                    className="h-16 w-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="font-bold text-lg">{post.author}</h3>
                    <p className="text-gray-600">{post.authorTitle}</p>
                    <p className="text-sm mt-1">
                      {post.author} is a SaaS industry veteran with over a decade of experience in building and scaling referral programs for businesses of all sizes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold">Share this article</h3>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm">Twitter</Button>
                <Button variant="outline" size="sm">LinkedIn</Button>
                <Button variant="outline" size="sm">Facebook</Button>
                <Button variant="outline" size="sm">Copy Link</Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default BlogPostDetail;
